import {
  Box,
  Checkbox,
  DialogContent,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import { t } from 'i18next';
import AddAccountProgressBar from '../shared/AddAccountProgressBar';
import ModalFooter from '../shared/ModalFooter';
import ModalSectionTitle from '../shared/ModalSectionTitle';
import { StepsForManual } from '../useAddAccountNavigation';
import ManualPortfolioDiversificationForm from './ManualPortfolioDiversificationForm';
import { PortfolioDiversificationFormState } from './usePortfolioDiversificationForm';
import CategoryUtility, { ClassificationItem } from '../categories.utility';
import { useMemo } from 'react';
import { RealEstateDetailsFormState } from '../ManualRealEstateDetails/useRealEstateDetailsForm';

interface ManualPortfolioDiversificationProps {
  handleOnCancel: () => void;
  footerLabels: any;
  updateModalStep: (step: StepsForManual) => void;
  selectedClassification: ClassificationItem;
  portfolioDiversificationForm: PortfolioDiversificationFormState;
  realEstateDetailsForm: RealEstateDetailsFormState;
  updatePortfolioDiversificationForm: (
    state: PortfolioDiversificationFormState,
  ) => void;
}

const ManualPortfolioDiversification = ({
  handleOnCancel,
  footerLabels,
  updateModalStep,
  selectedClassification,
  portfolioDiversificationForm,
  updatePortfolioDiversificationForm,
  realEstateDetailsForm,
}: ManualPortfolioDiversificationProps) => {
  const backStep: StepsForManual = useMemo(() => {
    return CategoryUtility.isRealEstate(selectedClassification.ClassificationId)
      ? StepsForManual.MANUAL_REAL_ESTATE_DETAILS
      : StepsForManual.MANUAL_ACCOUNT_DETAILS;
  }, [selectedClassification?.ClassificationId]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const state: PortfolioDiversificationFormState = {
      ...portfolioDiversificationForm,
      showDiversifications: event.target.checked,
      isValid: event.target.checked
        ? portfolioDiversificationForm.isValid
        : true,
    };

    updatePortfolioDiversificationForm(state);
  };

  return (
    <>
      <DialogContent>
        <Box sx={{ marginBottom: 3 }}>
          <AddAccountProgressBar
            activeStep={StepsForManual.MANUAL_PORTFOLIO_DIVERSIFICATION}
          />
          <ModalSectionTitle>
            {t('addAccountModal.portfolioDiversification.title')}
          </ModalSectionTitle>

          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
            {selectedClassification?.Name}
          </Typography>
        </Box>

        <Box sx={{ marginBottom: 6 }}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={portfolioDiversificationForm.showDiversifications}
                  onChange={handleChange}
                />
              }
              label="Set Account's Portfolio Allocation"
            />
          </FormGroup>

          <Typography variant='body2'>
            Adding a diversification category is optional. If you skip this
            step, this account's value will be included in your portfolio but
            won't appear in your diversification chart.
          </Typography>
        </Box>

        {portfolioDiversificationForm.showDiversifications && (
          <ManualPortfolioDiversificationForm
            onFormChange={updatePortfolioDiversificationForm}
            selectedClassification={selectedClassification!}
            realEstateDetailsForm={realEstateDetailsForm}
            portfolioDiversificationForm={portfolioDiversificationForm}
          />
        )}
      </DialogContent>

      <ModalFooter
        onCancel={handleOnCancel}
        onBack={() => updateModalStep(backStep)}
        label={{ ...footerLabels, primaryCta: footerLabels.continue }}
        onPrimaryAction={() =>
          updateModalStep(StepsForManual.MANUAL_HISTORICAL_VALUES)
        }
        isPrimaryActionDisabled={
          portfolioDiversificationForm.showDiversifications &&
          !portfolioDiversificationForm.isValid
        }
      />
    </>
  );
};

export default ManualPortfolioDiversification;
